import LoadError from '@/components/ui/LoadError.tsx';
import { sortByCurrency } from '@/types/stats/metric.utils.ts';
import { tourRoute } from '@/features/dashboard/tours/routes/tourRoute.ts';
import useGetTourDashboardSummary from '@/features/dashboard/tours/hooks/useGetTourDashboardSummary.ts';
import useGetTourFinanceDashboard from '@/features/dashboard/tours/hooks/useGetTourFinanceDashboard.ts';
import TourAttentionPanel from '@/features/dashboard/tours/components/attention/TourAttentionPanel.tsx';
import TourOccupancyCard from '@/features/dashboard/tours/components/summary/TourOccupancyCard.tsx';
import TourPaymentsCard from '@/features/dashboard/tours/components/summary/TourPaymentsCard.tsx';
import TourSignupPaceCard from '@/features/dashboard/tours/components/summary/TourSignupPaceCard.tsx';
import TourSummaryTabSkeleton from '@/features/dashboard/tours/pages/TourSummaryTabSkeleton.tsx';

/**
 * Pierwsze spojrzenie na wyjazd: ile miejsc zajętych, ile pieniędzy wpłynęło,
 * czym trzeba się zająć dziś i czy zapisy idą w tempie innych wyjazdów.
 *
 * Wpłaty przyjeżdżają z dashboardu finansowego, nie z podsumowania — te same
 * salda rysuje nagłówek ekranu, więc odpowiedź jest zwykle już w cache i drugi
 * hook nie dokłada oczekiwania.
 */
const TourSummaryTab = () => {
  const { tourId } = tourRoute.useParams();
  const {
    data: summary,
    isPending: isSummaryPending,
    isError: isSummaryError,
  } = useGetTourDashboardSummary(tourId);
  const {
    data: finance,
    isPending: isFinancePending,
    isError: isFinanceError,
  } = useGetTourFinanceDashboard(tourId);

  if (isSummaryPending || isFinancePending) return <TourSummaryTabSkeleton />;
  if (isSummaryError || isFinanceError || !summary || !finance)
    return <LoadError subject="podsumowania wyjazdu" />;

  const balances = sortByCurrency(finance.balances);

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <TourOccupancyCard
          occupancy={summary.occupancy}
          waitlistCount={summary.waitlistCount}
          tourId={tourId}
        />
        {balances.map((balance) => (
          <TourPaymentsCard
            key={balance.currency}
            balance={balance}
            tourId={tourId}
            showCurrency={balances.length > 1}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <TourAttentionPanel tourId={tourId} title="Wymaga uwagi" />
        <TourSignupPaceCard
          signups={summary.signups}
          occupancy={summary.occupancy}
        />
      </div>
    </div>
  );
};

export default TourSummaryTab;
